
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, Clock, Search, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Contact, ResearchTask } from '@/pages/PersonalizeOutreach';

interface ResearchProgressProps {
  contacts: Contact[];
  salesPitch: string;
  additionalContext?: string;
  researchTasks: ResearchTask[];
  setResearchTasks: (tasks: ResearchTask[] | ((prevTasks: ResearchTask[]) => ResearchTask[])) => void;
  onResearchCompleted: (contacts: Contact[]) => void;
}

const ResearchProgress = ({ 
  contacts, 
  salesPitch, 
  additionalContext,
  researchTasks, 
  setResearchTasks,
  onResearchCompleted 
}: ResearchProgressProps) => {
  const [updatedContacts, setUpdatedContacts] = useState<Contact[]>(contacts);

  const updateTaskStatus = useCallback((contactId: string, updates: Partial<ResearchTask>) => {
    setResearchTasks((prevTasks: ResearchTask[]) => 
      prevTasks.map(task => 
        task.contactId === contactId 
          ? { ...task, ...updates }
          : task
      )
    );
  }, [setResearchTasks]);

  const processContact = async (contact: Contact, pitch: string, context?: string) => {
    try {
      console.log('Processing contact:', contact.person);
      
      const { data, error } = await supabase.functions.invoke('research-contact', {
        body: {
          contact,
          salesPitch: pitch,
          additionalContext: context,
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error('Failed to research contact');
      }

      console.log('Research result:', data);
      
      return {
        ...contact,
        research: data.research,
        personalizedMessage: data.personalizedMessage,
      };
    } catch (error) {
      console.error('Error researching contact:', error);
      throw error;
    }
  };

  useEffect(() => {
    const runResearch = async () => {
      if (researchTasks.length === 0 || researchTasks.some(task => task.status === 'completed' || task.status === 'error')) {
        return;
      }

      console.log('Starting research for', contacts.length, 'contacts');
      const completedContacts: Contact[] = [];
      
      for (let i = 0; i < contacts.length; i++) {
        const contact = contacts[i];
        console.log('Processing contact', i + 1, 'of', contacts.length, ':', contact.person);
        
        // Update task status to researching
        updateTaskStatus(contact.id, { status: 'researching', progress: 25 });

        try {
          // Simulate research process with progress updates
          await new Promise(resolve => setTimeout(resolve, 1000));
          updateTaskStatus(contact.id, { progress: 50 });

          await new Promise(resolve => setTimeout(resolve, 1000));
          updateTaskStatus(contact.id, { progress: 75 });

          // Process the contact (this calls the actual Supabase edge function)
          const processedContact = await processContact(contact, salesPitch, additionalContext);
          completedContacts.push(processedContact);

          // Update task status to completed
          updateTaskStatus(contact.id, { status: 'completed', progress: 100 });

          toast.success(`Research completed for ${contact.person}`);
          
        } catch (error) {
          console.error('Error processing contact:', error);
          
          // Update task status to error
          updateTaskStatus(contact.id, { 
            status: 'error', 
            progress: 0, 
            error: 'Failed to research contact' 
          });

          // Add contact with empty research for failed cases
          completedContacts.push({
            ...contact,
            research: 'Research failed - please try again',
            personalizedMessage: 'Unable to generate personalized message',
          });

          toast.error(`Failed to research ${contact.person}`);
        }
      }

      console.log('Research completed for all contacts');
      setUpdatedContacts(completedContacts);
    };

    runResearch();
  }, [contacts, salesPitch, additionalContext, updateTaskStatus]);

  const completedTasks = researchTasks.filter(task => task.status === 'completed').length;
  const errorTasks = researchTasks.filter(task => task.status === 'error').length;
  const overallProgress = researchTasks.length > 0 ? (completedTasks + errorTasks) / researchTasks.length * 100 : 0;

  const handleContinue = () => {
    onResearchCompleted(updatedContacts);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Enhanced AI Research & Message Generation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Enhanced Research Process</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Deep company analysis and recent news discovery</li>
              <li>• Industry insights and competitive positioning</li>
              <li>• Pain point identification and value proposition mapping</li>
              <li>• Personalized message crafting with specific insights</li>
            </ul>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-gray-600">
                {completedTasks + errorTasks} of {researchTasks.length} completed
              </span>
            </div>
            <Progress value={overallProgress} className="h-2" />
          </div>

          <div className="grid gap-4">
            {researchTasks.map((task) => {
              const contact = contacts.find(c => c.id === task.contactId);
              if (!contact) return null;

              return (
                <div
                  key={task.contactId}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {task.status === 'completed' && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                      {task.status === 'error' && (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      )}
                      {task.status === 'researching' && (
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                      )}
                      {task.status === 'pending' && (
                        <Clock className="h-5 w-5 text-gray-400" />
                      )}
                      
                      <div>
                        <p className="font-medium">{contact.person}</p>
                        <p className="text-sm text-gray-600">{contact.organization}</p>
                      </div>
                    </div>
                    
                    <Badge 
                      variant={
                        task.status === 'completed' ? 'default' :
                        task.status === 'error' ? 'destructive' :
                        task.status === 'researching' ? 'secondary' : 'outline'
                      }
                    >
                      {task.status === 'completed' && 'Completed'}
                      {task.status === 'error' && 'Error'}
                      {task.status === 'researching' && 'Researching...'}
                      {task.status === 'pending' && 'Waiting'}
                    </Badge>
                  </div>
                  
                  {task.status !== 'pending' && (
                    <Progress value={task.progress} className="h-1" />
                  )}
                  
                  {task.error && (
                    <p className="text-sm text-red-600 mt-2">{task.error}</p>
                  )}
                </div>
              );
            })}
          </div>

          {overallProgress === 100 && (
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">Enhanced Research Complete!</span>
              </div>
              <p className="text-sm text-green-800">
                Deep AI research and personalized message generation has been completed for all contacts.
                {errorTasks > 0 && ` ${errorTasks} contact(s) had errors and may need manual review.`}
              </p>
            </div>
          )}

          {overallProgress === 100 && (
            <div className="flex justify-end pt-4">
              <Button onClick={handleContinue} size="lg" className="px-8">
                <MessageCircle className="h-4 w-4 mr-2" />
                Review Results
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResearchProgress;
